import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Play, Plus, X } from 'lucide-react';
import NewCustomDataTable from '../common/NewCustomDataTable.jsx';
import useScrollLoadMore from '../common/useScrollLoadMore';
import NewCustomAsyncSelectDropdown from '../common/NewCustomAsyncSelectDropdown.jsx';
import ExportDialog from '../common/Export/ExportDialog';
import { toast } from '../common/Toast/toast';
import {
  getReportSources,
  getReportSource,
  getReportFieldOptions,
  runReport,
  reportErrorMessage,
} from '../../../services/ReportGeneratorService';
import ReportChart from './ReportChart.jsx';
import '../Trainer/TrainerDashboard.css';
import '../common/lms-pages.css';
import './report-generator.css';

/* =============================================================
   REPORT GENERATOR

   One panel, read top to bottom like a sentence:
     [Students]  where  [Batch] [is] [Java Morning]   (filters)
     show  [Student] [Email] [Batch] ...              (columns)
     chart  [Number of students] split by [Course]    (chart)

   The page only assembles that definition --
   { source, conditions, columns, sort, chartBy, metric } -- and the API
   (modules/reports) filters, sorts, totals and pages it, limited to what
   this user may see: every batch for an administrator, their own for a
   trainer. Nothing here filters or adds up a row.

   Picking a tab runs the report straight away, so there is always
   something on screen to narrow down. A filter row without a value is not
   sent, and the panel says so beside it.

   Reached from a button on the admin and trainer dashboards; not in the
   sidebar yet.
   ============================================================= */

const PAGE_SIZE = 25;
const MAX_FILTERS = 20;
// list-attendance's filter dropdowns fetch 20 options a page.
const OPTION_PAGE_SIZE = 20;

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

const emptyValue = (field, op) => {
  if (!op || op.arity === 0) return null;
  if (op.arity === 'list') return [];
  if (op.arity === 'days') return 30;
  if (op.arity === 2) return ['', ''];
  return '';
};

const blankFilter = (uid, field, operators) => {
  const op = (operators[field.type] || [])[0];
  return { uid, field: field.key, op: op?.id, value: emptyValue(field, op) };
};

const isComplete = (filter, fieldsByKey, operators) => {
  const field = fieldsByKey.get(filter.field);
  const op = field ? (operators[field.type] || []).find((o) => o.id === filter.op) : null;
  if (!field || !op) return false;
  const v = filter.value;
  if (op.arity === 0) return true;
  if (op.arity === 'list') return Array.isArray(v) && v.length > 0;
  if (op.arity === 'days') return Number.isInteger(Number(v)) && Number(v) >= 1;
  if (op.arity === 2) return Array.isArray(v) && !isBlank(v[0]) && !isBlank(v[1]);
  return !isBlank(v);
};

const parseMetric = (key) => {
  if (!key || key === 'count') return { id: 'count' };
  const [id, field] = key.split(':');
  return { id, field };
};

const formatNumber = (value, unit) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const text = Number.isInteger(n)
    ? n.toLocaleString('en-IN')
    : n.toLocaleString('en-IN', { maximumFractionDigits: 1 });
  return unit ? `${text}${unit}` : text;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const optionText = (column, value) => {
  const found = (column.options || []).find((o) => o.value === value);
  return found ? found.label : String(value);
};

/** A cell as the table shows it. */
const formatCell = (column, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (column.type === 'number') return formatNumber(value, column.unit);
  if (column.type === 'date') return formatDate(value);
  if (column.type === 'boolean') return value === true ? 'Yes' : 'No';
  if (column.type === 'enum') return optionText(column, value);
  return String(value);
};

/** A cell as a spreadsheet wants it: numbers as numbers, dates as dates. */
const exportCell = (column, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (column.type === 'number') return value;
  if (column.type === 'date') return String(value).slice(0, 10);
  if (column.type === 'boolean') return value === true ? 'Yes' : 'No';
  if (column.type === 'enum') return optionText(column, value);
  return String(value);
};

const figureText = (s) => formatNumber(s.value, s.unit);

/* ── A choice value: the list-attendance filter dropdown ──
   Type in the control to search, scroll to the bottom for the next page.
   A searched list (field.remote: batches, courses, trainers, topics) is
   searched and paged by the API within this user's scope; a short fixed
   list (status, mode, band) arrives whole with the field. */
const ChoicePicker = ({ sourceId, field, multi, value, onChange }) => {
  const remote = Boolean(field.remote);
  const [options, setOptions] = useState(() => (remote ? [] : field.options || []));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(remote);
  const [search, setSearch] = useState('');
  const fetchSeqRef = useRef(0);

  const fetchOptions = useCallback(
    async (nextPage = 1, term = '', append = false, fresh = false) => {
      const requestId = ++fetchSeqRef.current;
      setLoading(true);
      try {
        const data = await getReportFieldOptions(sourceId, field.key, {
          search: term,
          page: nextPage,
          limit: OPTION_PAGE_SIZE,
          fresh,
        });
        // A newer search has gone out since; its answer is the one to keep.
        if (requestId !== fetchSeqRef.current) return;
        const next = data?.options || [];
        setOptions((prev) => (append ? [...prev, ...next] : next));
        setHasMore(Boolean(data?.pagination?.hasNext));
        setPage(nextPage);
      } catch (error) {
        console.error(`Error fetching ${field.key} options:`, error);
      } finally {
        if (requestId === fetchSeqRef.current) setLoading(false);
      }
    },
    [sourceId, field.key],
  );

  // A searched list's value is its label, so a chosen value draws as itself.
  const labelOf = (v) => (field.options || []).find((o) => o.value === v)?.label || v;
  const chosen = multi ? (Array.isArray(value) ? value : []) : isBlank(value) ? [] : [value];
  const selected = chosen.map((v) => ({ value: v, label: labelOf(v) }));
  const noun = field.label.toLowerCase();

  return (
    <NewCustomAsyncSelectDropdown
      options={options}
      value={selected}
      onChange={(list) =>
        multi ? onChange(list.map((o) => o.value)) : onChange(list[list.length - 1]?.value ?? '')
      }
      placeholder={multi ? `Pick one or more` : `Pick a ${noun}`}
      isMulti={multi}
      isLoading={loading}
      searchInControl={true}
      noOptionsMessage={`Nothing to choose from`}
      noSearchResultsMessage={`No ${noun} matches that search`}
      onMenuOpen={
        remote
          ? async () => {
            setPage(1);
            setSearch('');
            await fetchOptions(1, '', false);
          }
          : undefined
      }
      onInputChange={
        remote
          ? async (term) => {
            setSearch(term);
            setPage(1);
            await fetchOptions(1, term, false);
          }
          : undefined
      }
      onMenuScrollToBottom={
        remote
          ? async () => {
            if (hasMore && !loading) await fetchOptions(page + 1, search, true);
          }
          : undefined
      }
      onRefresh={
        remote
          ? async () => {
            setPage(1);
            setSearch('');
            await fetchOptions(1, '', false, true);
          }
          : undefined
      }
    />
  );
};

/* ── The value box, shaped by the field and the condition ── */
const ValueInput = ({ sourceId, field, op, value, onChange }) => {
  if (!op) return null;
  if (op.arity === 0) return <span className="rg-novalue">No value needed</span>;

  if (field.type === 'enum') {
    return (
      <ChoicePicker
        key={`${field.key}-${op.arity}`}
        sourceId={sourceId}
        field={field}
        multi={op.arity === 'list'}
        value={value}
        onChange={onChange}
      />
    );
  }

  if (op.arity === 'days') {
    return (
      <span className="rg-days">
        <input
          type="number"
          min="1"
          max="3650"
          step="1"
          className="pg-input rg-num"
          aria-label="Number of days"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
        <span className="rg-unit">days</span>
      </span>
    );
  }

  const inputType = field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text';

  if (op.arity === 2) {
    const pair = Array.isArray(value) ? value : ['', ''];
    return (
      <span className="rg-pair">
        <input
          type={inputType}
          className="pg-input"
          aria-label={`${field.label} from`}
          value={pair[0] ?? ''}
          onChange={(e) => onChange([e.target.value, pair[1]])}
        />
        <span className="rg-unit">and</span>
        <input
          type={inputType}
          className="pg-input"
          aria-label={`${field.label} to`}
          value={pair[1] ?? ''}
          onChange={(e) => onChange([pair[0], e.target.value])}
        />
      </span>
    );
  }

  return (
    <input
      type={inputType}
      className="pg-input"
      aria-label={`${field.label} value`}
      value={value ?? ''}
      placeholder={field.type === 'text' ? `Type a ${field.label.toLowerCase()}` : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/* ── One filter: field, condition, value ── */
const FilterRow = ({ sourceId, index, filter, fields, operators, complete, onChange, onRemove }) => {
  const field = fields.find((f) => f.key === filter.field) || fields[0];
  const ops = operators[field.type] || [];
  const op = ops.find((o) => o.id === filter.op) || ops[0];

  const setField = (key) => {
    const next = fields.find((f) => f.key === key);
    const firstOp = (operators[next.type] || [])[0];
    onChange({ ...filter, field: key, op: firstOp?.id, value: emptyValue(next, firstOp) });
  };

  const setOp = (id) => {
    const nextOp = ops.find((o) => o.id === id);
    // Keep what was typed when the value still fits the new condition.
    const sameShape = nextOp && op && nextOp.arity === op.arity;
    onChange({ ...filter, op: id, value: sameShape ? filter.value : emptyValue(field, nextOp) });
  };

  return (
    <div
      className={`rg-filter${complete ? '' : ' is-incomplete'}`}
      role="group"
      aria-label={`Filter ${index + 1}`}
    >
      <div className="pg-field">
        <select
          className="pg-select"
          aria-label="Field"
          value={field.key}
          onChange={(e) => setField(e.target.value)}
        >
          {fields.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="pg-field">
        <select
          className="pg-select"
          aria-label="Condition"
          value={op?.id || ''}
          onChange={(e) => setOp(e.target.value)}
        >
          {ops.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="pg-field rg-filter-value">
        <ValueInput
          sourceId={sourceId}
          field={field}
          op={op}
          value={filter.value}
          onChange={(value) => onChange({ ...filter, value })}
        />
      </div>

      <button
        type="button"
        className="rg-icon-btn"
        onClick={onRemove}
        aria-label={`Remove filter ${index + 1}`}
        title="Remove filter"
      >
        <X aria-hidden="true" />
      </button>
    </div>
  );
};

const ReportGenerator = () => {
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({ role: null, sources: [] });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  const [sourceId, setSourceId] = useState('');
  const [definition, setDefinition] = useState(null);
  const [definitionLoading, setDefinitionLoading] = useState(false);
  const [definitionError, setDefinitionError] = useState(null);

  const [filters, setFilters] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sort, setSort] = useState(null);
  const [chartBy, setChartBy] = useState('');
  const [metricKey, setMetricKey] = useState('count');

  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  /* A scroll batch, as distinct from a fresh run: the table veils itself
     for a run and must not for an append, or reading the rows you already
     have flickers every time the next page lands. */
  const [loadingMore, setLoadingMore] = useState(false);
  const appendRef = useRef(false);
  const listEndRef = useRef(null);
  const [runError, setRunError] = useState(null);
  const [lastRunKey, setLastRunKey] = useState('');

  const [exportData, setExportData] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const uidRef = useRef(0);
  const sourceRequestRef = useRef(0);
  const runRequestRef = useRef(0);
  // The definition that produced the report on screen. Paging and export
  // use it, not the controls, which may have been changed since.
  const ranDefinitionRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getReportSources();
        if (alive) setCatalog(data);
      } catch (error) {
        if (!alive) return;
        setCatalogError(
          error?.response?.status === 403
            ? 'forbidden'
            : reportErrorMessage(error, 'The report generator could not be loaded.'),
        );
      } finally {
        if (alive) setCatalogLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const execute = useCallback(async (reportDefinition, page) => {
    // Read before the first await, so a run started while a scroll batch
    // is in flight cannot pick up the other one's intent.
    const append = appendRef.current;
    const requestId = ++runRequestRef.current;
    if (append) setLoadingMore(true);
    else setRunning(true);
    setRunError(null);
    try {
      const data = await runReport({ ...reportDefinition, page });
      if (runRequestRef.current !== requestId) return;
      ranDefinitionRef.current = reportDefinition;
      setLastRunKey(JSON.stringify(reportDefinition));
      /* An appended page puts its rows after the ones on screen; the rest
         of the payload -- chart, figures, pagination -- is the newest
         answer and replaces wholesale. A fresh run replaces everything. */
      if (append) {
        setResult((prev) =>
          prev ? { ...data, rows: [...(prev.rows || []), ...(data.rows || [])] } : data,
        );
      } else {
        setResult(data);
      }
    } catch (error) {
      if (runRequestRef.current !== requestId) return;
      /* A failed SCROLL batch must not blank the rows already read -- the
         error state replaces the whole result. It says so in a toast and
         leaves the report standing. */
      if (append) toast.error(reportErrorMessage(error));
      else setRunError(reportErrorMessage(error));
    } finally {
      if (runRequestRef.current === requestId) {
        setRunning(false);
        setLoadingMore(false);
      }
      appendRef.current = false;
    }
  }, []);

  /* Infinite scroll rather than page numbers (list_page_rules): the foot
     under the table is the sentinel, and crossing it asks for the next
     page. Declared here, above the early returns below -- a hook cannot
     sit under one.

     Depends on loadingMore, which moves on every batch: an observer that
     is not rebuilt after a page lands never reports the sentinel again. */
  const loadedRows = result?.rows?.length || 0;
  const runPage = result?.pagination?.page || 1;
  const runTotalPages = result?.pagination?.totalPages || 1;
  const hasMoreRows = runPage < runTotalPages;

  useScrollLoadMore(
    listEndRef,
    () => {
      if (running || loadingMore || !hasMoreRows || !ranDefinitionRef.current) return;
      appendRef.current = true;
      execute(ranDefinitionRef.current, runPage + 1);
    },
    [loadedRows, runPage, runTotalPages, running, loadingMore, hasMoreRows],
  );

  const chooseSource = useCallback(
    async (id) => {
      const requestId = ++sourceRequestRef.current;
      runRequestRef.current += 1;
      ranDefinitionRef.current = null;
      setSourceId(id);
      setDefinition(null);
      setDefinitionError(null);
      setResult(null);
      setRunError(null);
      setRunning(false);
      setLastRunKey('');
      setDefinitionLoading(true);
      try {
        const data = await getReportSource(id);
        if (sourceRequestRef.current !== requestId) return;
        const operators = data?.operators || {};
        const firstField = (data?.fields || []).find((f) => f.filterable);
        uidRef.current += 1;
        const startColumns = data?.defaultColumns || [];
        const startSort = data?.defaultSort || null;
        const startChart = data?.defaultChart || '';

        setDefinition(data);
        // One empty filter, ready to fill in.
        setFilters(firstField ? [blankFilter(uidRef.current, firstField, operators)] : []);
        setColumns(startColumns);
        setSort(startSort);
        setChartBy(startChart);
        setMetricKey('count');

        // Every row straight away, before any filter. Same key order as
        // buildDefinition, so the report does not read as out of date.
        execute(
          {
            source: id,
            conditions: [],
            columns: startColumns,
            sort: startSort,
            chartBy: startChart,
            metric: { id: 'count' },
            limit: PAGE_SIZE,
          },
          1,
        );
      } catch (error) {
        if (sourceRequestRef.current !== requestId) return;
        setDefinitionError(reportErrorMessage(error, 'This data could not be loaded.'));
      } finally {
        if (sourceRequestRef.current === requestId) setDefinitionLoading(false);
      }
    },
    [execute],
  );

  // Open on the first tab, so the page never starts empty.
  useEffect(() => {
    if (!sourceId && catalog.sources.length) chooseSource(catalog.sources[0].id);
  }, [catalog.sources, sourceId, chooseSource]);

  const fields = useMemo(() => definition?.fields || [], [definition]);
  const operators = useMemo(() => definition?.operators || {}, [definition]);
  const fieldsByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);
  const filterFields = useMemo(() => fields.filter((f) => f.filterable), [fields]);
  const groupFields = useMemo(() => fields.filter((f) => f.groupable), [fields]);
  const columnFields = useMemo(() => fields.filter((f) => f.column), [fields]);
  const rowNoun = definition?.rowNoun || 'rows';

  const metricOptions = useMemo(() => {
    if (!definition) return [];
    const words = Object.fromEntries((definition.metrics || []).map((m) => [m.id, m.label]));
    return [
      { key: 'count', label: `Number of ${definition.rowNoun}` },
      ...fields
        .filter((f) => f.aggregatable)
        .flatMap((f) =>
          ['avg', 'sum', 'min', 'max'].map((id) => ({
            key: `${id}:${f.key}`,
            label: `${words[id] || id} ${f.label.toLowerCase()}`,
          })),
        ),
    ];
  }, [definition, fields]);

  const completeness = useMemo(
    () => filters.map((f) => isComplete(f, fieldsByKey, operators)),
    [filters, fieldsByKey, operators],
  );
  const unused = completeness.filter((done) => !done).length;

  const buildDefinition = useCallback(
    () => ({
      source: sourceId,
      // A filter without a value is not sent; the panel says it is unused.
      conditions: filters
        .filter((_, i) => completeness[i])
        .map(({ field, op, value }) => ({ field, op, value })),
      columns,
      sort,
      chartBy,
      metric: parseMetric(metricKey),
      limit: PAGE_SIZE,
    }),
    [sourceId, filters, completeness, columns, sort, chartBy, metricKey],
  );

  const definitionKey = useMemo(() => JSON.stringify(buildDefinition()), [buildDefinition]);
  const stale = Boolean(result) && lastRunKey !== '' && lastRunKey !== definitionKey;

  const addFilter = () => {
    const field = filterFields[0];
    if (!field) return;
    uidRef.current += 1;
    setFilters((prev) => [...prev, blankFilter(uidRef.current, field, operators)]);
  };

  const updateFilter = (uid, next) => setFilters((prev) => prev.map((f) => (f.uid === uid ? next : f)));

  const removeFilter = (uid) => setFilters((prev) => prev.filter((f) => f.uid !== uid));

  const toggleColumn = (key) => {
    setColumns((prev) => {
      const on = new Set(prev);
      if (on.has(key)) {
        if (on.size === 1) return prev; // a table needs at least one column
        on.delete(key);
      } else {
        on.add(key);
      }
      // Kept in the source's field order, not the order they were ticked.
      return columnFields.map((f) => f.key).filter((k) => on.has(k));
    });
  };

  const openExport = async () => {
    if (!ranDefinitionRef.current) return;
    setExporting(true);
    try {
      const data = await runReport({ ...ranDefinitionRef.current, exportAll: true });
      setExportData(data);
      setExportOpen(true);
    } catch (error) {
      toast.error(reportErrorMessage(error, 'The export could not be prepared.'));
    } finally {
      setExporting(false);
    }
  };

  const chartUnit = result?.chart?.unit || null;
  const chartFormatter = useCallback((value) => formatNumber(value, chartUnit), [chartUnit]);

  const headers = useMemo(
    () =>
      (result?.columns || []).map((column) => ({
        title: column.label,
        prop: column.key,
        isSortable: false,
        cell: (row) => (
          <span className={column.type === 'number' ? 'rg-num-cell' : undefined}>
            {formatCell(column, row[column.key])}
          </span>
        ),
      })),
    [result],
  );

  const exportColumns = useMemo(
    () =>
      (exportData?.columns || []).map((column) => ({
        key: column.key,
        label: column.unit ? `${column.label} (${column.unit})` : column.label,
        value: (row) => exportCell(column, row[column.key]),
      })),
    [exportData],
  );

  const exportPreamble = useMemo(() => {
    if (!exportData) return [];
    return [
      `${exportData.source.label} report`,
      `Generated: ${formatDateTime(exportData.generatedAt)}`,
      exportData.conditions.length
        ? `Filters: ${exportData.conditions.map((c) => c.text).join('; ')}`
        : 'Filters: none',
      `Figures: ${exportData.summary.map((s) => `${s.label} ${figureText(s)}`).join(' | ')}`,
    ];
  }, [exportData]);

  const dashboardPath = catalog.role === 'trainer' ? '/trainer-dashboard' : '/dashboard';

  if (catalogLoading) {
    return (
      <div className="pg lp rg">
        <div className="pg-state">
          <span className="pg-spinner" role="status" aria-hidden="true" />
          <p className="pg-state-text">Loading the report generator...</p>
        </div>
      </div>
    );
  }

  if (catalogError) {
    return (
      <div className="pg lp rg">
        <div className="pg-state">
          <h3 className="pg-state-title">
            {catalogError === 'forbidden' ? 'Reports are for administrators and trainers' : 'The report generator did not load'}
          </h3>
          <p className="pg-state-text">
            {catalogError === 'forbidden'
              ? 'Your account does not have access to the report generator.'
              : catalogError}
          </p>
          <button type="button" className="pg-btn" onClick={() => navigate('/')}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const pagination = result?.pagination;

  return (
    <div className="pg lp rg">
      <header className="pg-head">
        <div className="pg-head-main">
          <button type="button" className="rg-back" onClick={() => navigate(dashboardPath)}>
            <ArrowLeft aria-hidden="true" />
            Back to dashboard
          </button>
          <h1 className="pg-title">Report generator</h1>
          <p className="pg-sub">
            Choose the data, add filters, tick the columns you want and run the report.{' '}
            {catalog.role === 'trainer' ? 'Built from your own batches.' : 'Built across every batch.'}
          </p>
        </div>
      </header>

      <div className="rg-builder">
        <div className="lp-tabs rg-tabs" role="tablist" aria-label="What to report on">
          {catalog.sources.map((s) => (
            <button
              type="button"
              role="tab"
              key={s.id}
              aria-selected={sourceId === s.id}
              className={`lp-tab${sourceId === s.id ? ' is-on' : ''}`}
              title={s.description}
              onClick={() => {
                if (s.id !== sourceId) chooseSource(s.id);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <section className="pg-filters rg-panel" aria-label="Report settings">
          {definitionLoading && (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading the fields...</p>
            </div>
          )}

          {definitionError && (
            <div className="pg-state">
              <h3 className="pg-state-title">This data did not load</h3>
              <p className="pg-state-text">{definitionError}</p>
              <button type="button" className="pg-btn" onClick={() => chooseSource(sourceId)}>
                Try again
              </button>
            </div>
          )}

          {definition && (
            <>
              {/* Filters */}
              <div className="rg-block">
                <div className="rg-block-head">
                  <h2 className="rg-block-title">Filters</h2>
                  <span className="rg-block-hint">
                    Show {rowNoun} where every filter matches
                  </span>
                </div>

                {filters.length === 0 ? (
                  <p className="rg-empty">No filters: the report includes all {rowNoun} you can see.</p>
                ) : (
                  <div className="rg-filters">
                    <div className="rg-filter rg-filter-head" aria-hidden="true">
                      <span className="pg-label">Field</span>
                      <span className="pg-label">Condition</span>
                      <span className="pg-label">Value</span>
                      <span />
                    </div>
                    {filters.map((f, i) => (
                      <FilterRow
                        key={f.uid}
                        sourceId={sourceId}
                        index={i}
                        filter={f}
                        fields={filterFields}
                        operators={operators}
                        complete={completeness[i]}
                        onChange={(next) => updateFilter(f.uid, next)}
                        onRemove={() => removeFilter(f.uid)}
                      />
                    ))}
                  </div>
                )}

                <div className="rg-block-foot">
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={addFilter}
                    disabled={filters.length >= MAX_FILTERS || !filterFields.length}
                  >
                    <Plus aria-hidden="true" />
                    Add filter
                  </button>
                  {unused > 0 && (
                    <span className="rg-note">
                      {unused === 1
                        ? 'The marked filter has no value yet, so it is not used.'
                        : `${unused} marked filters have no value yet, so they are not used.`}
                    </span>
                  )}
                </div>
              </div>

              {/* Columns */}
              <div className="rg-block">
                <div className="rg-block-head">
                  <h2 className="rg-block-title" id="rg-columns-title">Columns</h2>
                  <span className="rg-block-hint">What the table and the export show</span>
                </div>
                <div className="rg-multi" role="group" aria-labelledby="rg-columns-title">
                  {columnFields.map((f) => {
                    const on = columns.includes(f.key);
                    return (
                      <button
                        type="button"
                        key={f.key}
                        className={`rg-chip${on ? ' is-on' : ''}`}
                        aria-pressed={on}
                        disabled={on && columns.length === 1}
                        onClick={() => toggleColumn(f.key)}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Chart, sort and the actions */}
              <div className="pg-filters-row rg-actions-row">
                <div className="pg-filters-fields rg-shape">
                  <div className="pg-field">
                    <label className="pg-label" htmlFor="rg-metric">
                      Chart shows
                    </label>
                    <select
                      id="rg-metric"
                      className="pg-select"
                      value={metricKey}
                      onChange={(e) => setMetricKey(e.target.value)}
                    >
                      {metricOptions.map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="rg-chart-by">
                      Split by
                    </label>
                    <select
                      id="rg-chart-by"
                      className="pg-select"
                      value={chartBy}
                      onChange={(e) => setChartBy(e.target.value)}
                    >
                      {groupFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="rg-sort">
                      Sort table by
                    </label>
                    <select
                      id="rg-sort"
                      className="pg-select"
                      value={sort?.field || ''}
                      onChange={(e) => setSort({ field: e.target.value, dir: sort?.dir || 'asc' })}
                    >
                      {columnFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-field">
                    <label className="pg-label" htmlFor="rg-order">
                      Order
                    </label>
                    <select
                      id="rg-order"
                      className="pg-select"
                      value={sort?.dir || 'asc'}
                      onChange={(e) => setSort({ field: sort?.field || columnFields[0]?.key, dir: e.target.value })}
                    >
                      <option value="asc">A to Z, low to high</option>
                      <option value="desc">Z to A, high to low</option>
                    </select>
                  </div>
                </div>

                <div className="pg-filters-actions">
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={() => execute(buildDefinition(), 1)}
                    disabled={running || !columns.length}
                  >
                    <Play aria-hidden="true" />
                    {running ? 'Running...' : 'Run report'}
                  </button>
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setFilters([])}
                    disabled={!filters.length}
                  >
                    Clear filters
                  </button>
                  <span className="pg-filters-sep" aria-hidden="true" />
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={openExport}
                    disabled={!result || exporting || running}
                  >
                    <Download aria-hidden="true" />
                    {exporting ? 'Preparing...' : 'Export'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <section className="rg-result" aria-live="polite" aria-busy={running}>
        {runError && (
          <div className="pg-state">
            <h3 className="pg-state-title">The report did not run</h3>
            <p className="pg-state-text">{runError}</p>
          </div>
        )}

        {running && !result && (
          <div className="pg-state">
            <span className="pg-spinner" role="status" aria-hidden="true" />
            <p className="pg-state-text">Working out the report...</p>
          </div>
        )}

        {result && (
          <>
            <div className="rg-result-head">
              <h2 className="rg-result-title">{result.source.label}</h2>
              <p className="rg-result-meta">
                {formatNumber(result.rowCount)} {result.source.rowNoun} · generated{' '}
                {formatDateTime(result.generatedAt)}
                {stale && (
                  <span className="rg-stale"> · settings changed, run the report to update</span>
                )}
              </p>
            </div>

            {result.conditions.length > 0 && (
              <ul className="rg-applied" aria-label="Filters applied">
                {result.conditions.map((c) => (
                  <li key={`${c.field}-${c.op}-${JSON.stringify(c.value)}`} className="pg-pill">
                    {c.text}
                  </li>
                ))}
              </ul>
            )}

            <div className="br-stats">
              {result.summary.map((s) => (
                <div className="br-stat" key={s.key}>
                  <span className="br-stat-label">{s.label}</span>
                  <span className="br-stat-value">{figureText(s)}</span>
                </div>
              ))}
            </div>

            <ReportChart chart={result.chart} formatValue={chartFormatter} />

            {result.rows.length === 0 ? (
              <div className="pg-state">
                <h3 className="pg-state-title">No rows match</h3>
                <p className="pg-state-text">Loosen or remove a filter and run the report again.</p>
              </div>
            ) : (
              <div className="td-list rg-table">
                <NewCustomDataTable
                  data={result.rows}
                  headers={headers}
                  totalRecords={pagination?.total || result.rows.length}
                  loading={running}
                  loadingMore={loadingMore}
                  showToolbar={false}
                  showPagination={false}
                  showFooter={false}
                />
              </div>
            )}

            {/* Where the reader is, and the sentinel the observer watches.
                This was Previous / Next / "Page 2 of 9", which a list page
                does not have -- the rows grow as you reach the foot
                (list_page_rules). Rendered only with the list, because the
                sentinel must not exist when there is nothing to page. */}
            {result.rows.length > 0 && (
              <div className="pg-scroll-foot" ref={listEndRef}>
                {pagination && result.rows.length < (pagination.total || 0) ? (
                  <>
                    <span className="pg-scroll-count">
                      {formatNumber(result.rows.length)} of {formatNumber(pagination.total)}
                    </span>
                    {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
                  </>
                ) : (
                  <span className="pg-scroll-count">
                    All {formatNumber(pagination?.total || result.rows.length)} rows loaded
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title={exportData ? `${exportData.source.label} report` : 'Report'}
        columns={exportColumns}
        rows={exportData?.rows || []}
        filenameBase={exportData ? `${exportData.source.id}-report` : 'report'}
        sheetName={exportData ? `${exportData.source.label} report` : 'Report'}
        preamble={exportPreamble}
        scopeNote={
          exportData?.truncated
            ? `Only the first ${formatNumber(exportData.rows.length)} of ${formatNumber(exportData.pagination.total)} rows fit in one export.`
            : undefined
        }
      />
    </div>
  );
};

export default ReportGenerator;
